// SPDX-License-Identifier: LGPL-3.0-or-later
import { RwLockable } from 'kerium/locks';
import { Resource } from 'utilium/cache';
import { InodeFlags, isBlockDevice, isCharacterDevice } from '../internal/inode.js';
/**
 * A VFS-level node, roughly equivalent to Linux's in-memory `struct inode`.
 *
 * A `VNode` is the single authoritative copy of an inode's metadata and cached data at the VFS level.
 * All open `Handle`s for the same file share one `VNode`, so metadata changes are immediately visible across handles.
 * File data written through a `VNode` is cached and only written to the backend when the vnode is synced.
 *
 * `VNode` methods do *not* acquire the vnode's lock. Callers are responsible for locking, since operations usually span multiple vnode calls.
 * @category VFS
 */
export class VNode extends RwLockable {
    fs;
    inode;
    /** Cached file data. This is a page cache, but with byte granularity */
    data;
    /** Ranges of `data` that have been written but not yet synced to the backend */
    dirtyRanges = [];
    /** The smallest size this vnode has been truncated to since the last sync, if it has been truncated */
    truncatedTo;
    /** Whether `inode` has changes that have not been synced to the backend */
    metadataDirty = false;
    /** The number of active references: open handles and in-progress VFS operations */
    refs = 0;
    /** Paths on `fs` that refer to this vnode. Multiple paths refer to the same vnode with hard links */
    paths = new Set();
    /** Fallback for when the last path is removed (i.e. the file is unlinked while open) */
    _lastPath;
    constructor(fs, path, inode) {
        super();
        this.fs = fs;
        this.inode = inode;
        this.paths.add(path);
        this._lastPath = path;
        this.data = new Resource(inode.ino, inode.size, { sparse: true });
    }
    get ino() {
        return this.inode.ino;
    }
    /** A path that can be used to address the backend */
    get path() {
        const [path] = this.paths;
        if (path !== undefined)
            this._lastPath = path;
        return path ?? this._lastPath;
    }
    /** Whether this vnode has data or metadata that has not been synced to the backend */
    get dirty() {
        return this.metadataDirty || this.dirtyRanges.length > 0;
    }
    /** Character and block devices bypass the data cache */
    get bypassCache() {
        return isCharacterDevice(this.inode) || isBlockDevice(this.inode) || !!(this.inode.flags & InodeFlags.DAX);
    }
    /** Add a range to `dirtyRanges`, merging overlapping and adjacent ranges */
    markDirty(start, end) {
        this.dirtyRanges.push({ start, end });
        this.dirtyRanges.sort((a, b) => a.start - b.start);
        const merged = [];
        for (const range of this.dirtyRanges) {
            const last = merged.at(-1);
            if (last && range.start <= last.end)
                last.end = Math.max(last.end, range.end);
            else
                merged.push(range);
        }
        this.dirtyRanges = merged;
    }
    /**
     * Copy `[start, end)` from the cache into `buffer`.
     * Uncached spans are holes (e.g. from an extending truncate) and are zero-filled.
     */
    copyFromCache(buffer, start, end) {
        let pos = start;
        for (const region of this.data.regions) {
            const regionEnd = region.offset + region.data.byteLength;
            if (regionEnd <= pos)
                continue;
            if (region.offset >= end)
                break;
            if (region.offset > pos) {
                buffer.fill(0, pos - start, region.offset - start);
                pos = region.offset;
            }
            const copyEnd = Math.min(end, regionEnd);
            buffer.set(region.data.subarray(pos - region.offset, copyEnd - region.offset), pos - start);
            pos = copyEnd;
        }
        if (pos < end)
            buffer.fill(0, pos - start, end - start);
    }
    /**
     * Read `[start, end)` into `buffer`, fetching any missing ranges from the backend.
     */
    async read(buffer, start, end) {
        if (this.bypassCache)
            return await this.fs.read(this.path, buffer, start, end);
        for (const { start: rangeStart, end: rangeEnd } of this.data.missing(start, end)) {
            const chunk = new Uint8Array(rangeEnd - rangeStart);
            await this.fs.read(this.path, chunk, rangeStart, rangeEnd);
            this.data.add(chunk, rangeStart);
        }
        this.copyFromCache(buffer, start, end);
    }
    readSync(buffer, start, end) {
        if (this.bypassCache)
            return this.fs.readSync(this.path, buffer, start, end);
        for (const { start: rangeStart, end: rangeEnd } of this.data.missing(start, end)) {
            const chunk = new Uint8Array(rangeEnd - rangeStart);
            this.fs.readSync(this.path, chunk, rangeStart, rangeEnd);
            this.data.add(chunk, rangeStart);
        }
        this.copyFromCache(buffer, start, end);
    }
    /**
     * Write `data` at `offset` into the cache and update metadata.
     * The backend is not written to until `sync`, except for devices, which bypass the cache.
     */
    async write(data, offset) {
        if (this.bypassCache)
            await this.fs.write(this.path, data, offset);
        else
            this._writeCached(data, offset);
        this._writeMetadata(offset + data.byteLength);
    }
    writeSync(data, offset) {
        if (this.bypassCache)
            this.fs.writeSync(this.path, data, offset);
        else
            this._writeCached(data, offset);
        this._writeMetadata(offset + data.byteLength);
    }
    _writeCached(data, offset) {
        const end = offset + data.byteLength;
        // Copy since the region can keep a reference and the caller may reuse the buffer
        this.data.add(data.slice(), offset);
        if (end > this.data.size)
            this.data.size = end;
        this.markDirty(offset, end);
    }
    _writeMetadata(end) {
        if (!this.bypassCache && end > this.inode.size)
            this.inode.size = end;
        this.inode.mtimeMs = Date.now();
        this.inode.ctimeMs = Date.now();
        this.metadataDirty = true;
    }
    /**
     * Change the size of the file.
     * Note an extending truncate creates a hole, which reads as zeroes.
     */
    truncate(length) {
        this.truncatedTo = Math.min(this.truncatedTo ?? length, length);
        this.data.size = length;
        this.dirtyRanges = this.dirtyRanges
            .filter(range => range.start < length)
            .map(range => (range.end > length ? { start: range.start, end: length } : range));
        this.inode.size = length;
        this.inode.mtimeMs = Date.now();
        this.inode.ctimeMs = Date.now();
        this.metadataDirty = true;
    }
    /**
     * Whether the backend has to be told about a truncation before the dirty data is written.
     */
    get needsPreTruncate() {
        return !this.bypassCache && this.truncatedTo !== undefined && this.truncatedTo < this.inode.size && !this.fs.attributes.has('no_write');
    }
    /** Write all unsynced data to the backend, then metadata */
    async sync() {
        if (this.needsPreTruncate)
            await this.fs.touch(this.path, { size: this.truncatedTo });
        this.truncatedTo = undefined;
        for (const range of this.dirtyRanges) {
            for (let pos = range.start; pos < range.end;) {
                const region = this.data.regionAt(pos);
                if (!region)
                    break; // should not happen since dirty ranges are always cached
                const end = Math.min(range.end, region.offset + region.data.byteLength);
                await this.fs.write(this.path, region.data.subarray(pos - region.offset, end - region.offset), pos);
                pos = end;
            }
        }
        this.dirtyRanges = [];
        if (!this.metadataDirty)
            return;
        if (!this.fs.attributes.has('no_write'))
            await this.fs.touch(this.path, this.inode);
        this.metadataDirty = false;
    }
    syncSync() {
        if (this.needsPreTruncate)
            this.fs.touchSync(this.path, { size: this.truncatedTo });
        this.truncatedTo = undefined;
        for (const range of this.dirtyRanges) {
            for (let pos = range.start; pos < range.end;) {
                const region = this.data.regionAt(pos);
                if (!region)
                    break; // should not happen since dirty ranges are always cached
                const end = Math.min(range.end, region.offset + region.data.byteLength);
                this.fs.writeSync(this.path, region.data.subarray(pos - region.offset, end - region.offset), pos);
                pos = end;
            }
        }
        this.dirtyRanges = [];
        if (!this.metadataDirty)
            return;
        if (!this.fs.attributes.has('no_write'))
            this.fs.touchSync(this.path, this.inode);
        this.metadataDirty = false;
    }
}
